import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { getBanksList, getOfferings } from 'services/api'
import { useGlobalContext } from 'providers/GlobalProvider'
import { isSuccessStatus } from 'utils/functions/validations'

import { IOfferingsB, IResponseGetAccount, IResponseDataAccount } from 'services/IApi.d'
import useGa from 'hooks/ga/useGa'
import { IOfferingsValues, IStore, ILoadingOffers, IAccountProvider } from './IOfferingProvider.d'


const OfferingContext = createContext(null)

interface IOfferingProps {
  children: React.ReactNode,
  initData: any,
}

const OfferingProvider = ({ children, initData }: IOfferingProps) => {
  const navigate = useNavigate()
  const defaultOfferingsValues: IOfferingsValues = {
    plan: null,
    accounts: [],
    additionalServices: []
  }

  const initLoadingValues: ILoadingOffers = {
    plan: true,
    accounts: true,
    additionalServices: true,
  }

  const { cuit, idEmpresa } = useGlobalContext()
  const { sendDataLayer } = useGa()
  const [selectedOfferings, setSelectedOfferings] = useState<IOfferingsValues>(defaultOfferingsValues)
  const [changePlan, setChangePlan] = useState<boolean>(false)
  const [offerings, setOfferings] = useState<IOfferingsB[]>(null)
  const [accounts, setAccounts] = useState<IResponseDataAccount[]>(null)
  const [accountError, setAccountError] = useState<boolean>(false)
  const [noCuitError, setNoCuitError] = useState<boolean>(false)
  const [selectedAccounts, setSelectedAccounts] = useState<IAccountProvider[]>([])
  const [checkedItems, setCheckedItems] = useState<any[]>([])

  const loading = useRef<ILoadingOffers>(initLoadingValues)
  const changeLoadingPlan = (val: boolean): void => {
    loading.current = { ...loading.current, plan: val }
  }
  const changeLoadingAccounts = (val: boolean): void => {
    loading.current = { ...loading.current, accounts: val }
  }
  const changeLoadingAdditionalServices = (val: boolean): void => {
    loading.current = { ...loading.current, additionalServices: val }
  }

  const store = useMemo(() => ({
    ...initData,
    selectedOfferings,
    setSelectedOfferings,
    selectedAccounts,
    setSelectedAccounts,
    checkedItems,
    setCheckedItems,
    offerings,
    accounts,
    accountError,
    noCuitError,
    loading,
    changePlan,
    setChangePlan,
    setAccounts,
    changeLoadingPlan,
    changeLoadingAccounts,
    changeLoadingAdditionalServices,
    sendDataLayer,
  }), [initData, checkedItems, setCheckedItems, selectedOfferings, selectedAccounts, setSelectedAccounts, changePlan, setChangePlan, setSelectedOfferings, loading, offerings, accounts, accountError, noCuitError])

  const getAccounts = async () => {
    try {
      const response: IResponseGetAccount = await getBanksList(cuit, idEmpresa)
      if (response.status === 200) {
        setAccounts(response.data)
      } else if (response.status === 409) {
        setNoCuitError(true)
      } else {
        setAccountError(true)
      }
    } catch (error) {
      setAccountError(true)
      setNoCuitError(true)
    }
  }

  useEffect(() => {
    getOfferings().then(response => {
      if (!isSuccessStatus(response.status)) {  
        navigate('/error')
      }
      setOfferings(response.data)
    })
    getAccounts()
  }, [])

  return (
    <OfferingContext.Provider value={store}>{children}</OfferingContext.Provider>
  )
}

const useOfferingProvider = () => {
  const context: IStore = useContext(OfferingContext)
  if (context === undefined) {
    throw new Error('PlanAccountsContext must be used within a PlanAccounts')
  }
  return context
}

export default OfferingProvider
export { useOfferingProvider }
